import React, { useEffect, useRef, useState } from "react";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { Steps } from "intro.js-react";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import useTour from "./hooks/useTour.js";
import { tours } from "./tours/tours.js";
import PropTypes from "prop-types";
import Splashscreen from "./components/Splashscreen.js";
import { callActionsForNextTourStep } from "./helpers/callActionsForNextTourStep.js";
import { changeStepNumbersDisplay } from "./helpers/changeStepNumbersDisplay.js";
import { setIsTourAlreadyShown } from "./helpers/setIsTourAlreadyShown.js";

import "intro.js/introjs.css";
import "./styles.scss";

/**
 * How to create a new tour:
 *
 * 1. Add new data (CSS selectors and events if it is necessary) inside ./tours/tours.js
 * 2. (optional) Create [tourName]Tour.scss file within ./tours directory, write styles for each step
 *    and import it to the ./styles.scss
 * 3. Add <ProductTourWrapper/> component where do you want
 *
 *  Step title text key example (use this key on the server side):
 *  0_tour_inbox_title
 *
 *  Step description text key example (use this key on the server side):
 *  0_tour_inbox_text
 *
 *  Step tooltip element CSS className example:
 *  .tooltip_0_tour_inbox or .tooltip_1_tour_inbox_rtl
 *
 *  Step highlight element CSS className example:
 *  .highlight_0_tour_inbox or .highlight_0_tour_inbox_rtl
 */

const ProductTour = ({ tourName, tourID, setIsShownFirstTime }) => {
  const { getTextByKey, getDirection } = useContent();
  const { getUserData } = useUser();
  const stepsRef = useRef(null);

  // Make steps from CSS selectors
  const tourElementSelectors = Object.keys(tours[tourName]);
  let steps = useTour(tourName, tourElementSelectors);

  // Check if "Take a tour" button clicked
  const [isTourStarted, setIsTourStarted] = useState(false);

  // intro.js tour preset
  const options = {
    showStepNumbers: true,
    nextLabel: getTextByKey("tour_next"),
    prevLabel: getTextByKey("tour_prev"),
    doneLabel: getTextByKey("tour_done"),
    hidePrev: true,
    disableInteraction: true,
    exitOnOverlayClick: false,
    exitOnEsc: true,
    keyboardNavigation: true,
    highlightClass: `highlight-element-${getDirection()}`,
  };

  // Do some actions before next step will occur
  const onBeforeStepChange = (nextStepIndex) => {
    changeStepNumbersDisplay(nextStepIndex, steps.length);
    callActionsForNextTourStep(nextStepIndex, stepsRef, tourName);
  };

  // Finish tour if something went wrong
  const onPreventChange = () => {
    if (tourName !== "inbox") {
      setIsTourAlreadyShown(tourID);
    }
    setIsShownFirstTime(false);
  };

  const exitTour = () => {
    if (tourName !== "inbox") {
      setIsTourAlreadyShown(tourID);
    }
    setIsShownFirstTime(false);
  };

  const skipTour = (e) => {
    e.preventDefault();

    setIsTourAlreadyShown(tourID);
    setIsShownFirstTime(false);
  };

  const startTour = () => {
    setIsTourAlreadyShown(tourID);
    setIsTourStarted(true);
  };

  return (
    <>
      {!!getUserData().UserId &&
        (!isTourStarted && tourName === "inbox" ? (
          <Splashscreen
            tourName={tourName}
            handleStart={startTour}
            handleSkip={skipTour}
          />
        ) : (
          <Steps
            ref={stepsRef}
            enabled
            steps={steps}
            initialStep={0}
            options={options}
            onExit={exitTour}
            onBeforeChange={onBeforeStepChange}
            onPreventChange={onPreventChange}
          />
        ))}
    </>
  );
};

ProductTour.defaultProps = {
  isPageReady: true,
};

ProductTour.propTypes = {
  isPageReady: PropTypes.bool,
};

export default ProductTour;
